import React from 'react';
import Vimeo from '@vimeo/player';
import { usePresentationContext } from '@mentimeter/question-modules-contexts';
import { Box } from '@mentimeter/ragnar-ui/box';
import type { VideoState } from '../../utils';

interface VimeoPlayerProps {
  embedUrl: string;
  startTime?: number;
}

export const VimeoPlayer = ({ startTime, embedUrl }: VimeoPlayerProps) => {
  const { useSlideState } = usePresentationContext();
  const [{ videoState }, setSlideState] = useSlideState<VideoState>();
  const [videoReady, setVideoReady] = React.useState(false);
  const playerRef = React.useRef<Vimeo | null>(null);
  const playerElementRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    async function playAfterReady(player: Vimeo) {
      await player
        .ready()
        .then(() => {
          player.play();
          if (startTime) {
            player.setCurrentTime(startTime);
          }
        })
        .catch(() => {
          setVideoReady(false);
        });
    }

    if (!playerRef.current && playerElementRef.current) {
      const player = new Vimeo('vimeo-player', {
        url: embedUrl,
        height: playerElementRef.current.clientHeight,
        width: playerElementRef.current.clientWidth,
      });
      player
        .ready()
        .then(() => {
          setVideoReady(true);
        })
        .catch(() => {
          // If video is not found, set state to closed
          setVideoReady(false);
          setSlideState({ videoState: 'closed' });
        });

      //When modal opens video should start playing
      setTimeout(() => {
        playAfterReady(player);
      }, 1000);

      player.on('play', () => {
        setSlideState({
          videoState: 'playing',
        });
      });

      player.on('pause', () => {
        setSlideState({
          videoState: 'paused',
        });
      });
      playerRef.current = player;
    }
  }, [setSlideState, startTime, embedUrl]);

  // listen to slideState changes
  React.useEffect(() => {
    async function play(player: Vimeo) {
      await player.getPaused().then((paused) => {
        if (!paused) return;
        player.play();
      });
    }

    async function pause(player: Vimeo) {
      await player.getPaused().then((paused) => {
        if (paused) return;
        player.pause();
      });
    }

    if (!playerRef.current || !videoReady) return;
    if (videoState === 'playing') {
      play(playerRef.current);
    } else if (videoState === 'paused') {
      pause(playerRef.current);
    }
  }, [videoReady, videoState]);

  return (
    <Box
      width="75%"
      height="75%"
      ref={playerElementRef}
      id="vimeo-player"
      data-testid="vimeo-player"
    />
  );
};
